import {
  Button,
  Col,
  ConfigProvider,
  Flex,
  Form,
  Grid,
  Modal,
  Result,
  Row,
  Skeleton,
} from "antd";
import enUS from "antd/locale/en_US";
import { FormikProps, useFormik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";

import { createJobPostingEventMetaData } from "./analytics";
import { AnswerInput } from "./components/AnswerInput";
import { ComplianceQuestions } from "./components/ComplianceQuestions";
import { Header } from "./components/Header";
import { H2ApplyTitle, RichTextContainer } from "./components/styled";
import { JOB_TYPE_BE_TO_FE_MAP } from "./constants";
import { getUrlJobPostingId, htmlDecode } from "./utils";

import { analyticsEvent } from "~/Analytics";
import { JobPostingSchema } from "~/Applet/SeoMeta/JobPostingsSchema";
import { APPLET_MANIFEST } from "~/AppletManifest";
import { AppletWrapper } from "~/Atom/AppletWrapper";
import { SanitizedHtml } from "~/Atom/SanitizedHTML";
import { EMAIL_REGEX } from "~/Constant";
import { GlobalStyles, themeConfig } from "~/Constant/Theme";
import { isDevelopment } from "~/env";
import { useFlag } from "~/LaunchDarkly";
import { useOmnichannelApi } from "~/OmnichannelApi";
import { Components, Paths } from "~/OmnichannelApi/openapi";

export const Applet: React.FC<{
  jobId: string;
  requestId?: string;
}> = ({ jobId, requestId }) => {
  const hasAuthorization = useFlag("applet-es-job-posting");

  const api = useOmnichannelApi();

  const [jobData, setJobData] = useState<Paths.JobsGet.Responses.$200>();
  const [jobLocations, setJobLocations] = useState<string[]>([]);

  const [showQuestions, setShowQuestions] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const targetRef = useRef(null);

  const breakpoints = Grid.useBreakpoint();

  const formik = useFormik<Components.Schemas.JobApplication>({
    initialValues: {
      first_name: "",
      last_name: "",
      email: "",
      // additional dynamic values will get added by formik at runtime
    },
    onSubmit: (values) =>
      api["jobs.application.create"](jobId, {
        ...values,
        // pass gh_src param for attribution
        mapped_url_token: new URLSearchParams(window.location.search).get(
          "gh_src"
        ),
      })
        .then(() => {
          setIsSubmitted(true);
        })
        .catch(console.error),
  });

  useEffect(() => {
    if (formik.dirty) {
      setIsDirty(true);
    }
  }, [formik.dirty]);

  useEffect(() => {
    if (jobData && isDirty) {
      analyticsEvent(
        "position_apply_start",
        createJobPostingEventMetaData(jobData)
      );

      if (requestId) {
        api["jobs.event.create"](undefined, {
          request_id: requestId,
          job_id: jobData.google.job.name,
          event_type: "APPLICATION_START",
        });
      }
    }
  }, [isDirty]);

  useEffect(() => {
    if (jobData && isSubmitted) {
      analyticsEvent(
        "position_apply_success",
        createJobPostingEventMetaData(jobData)
      );

      document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" });

      if (requestId) {
        api["jobs.event.create"](undefined, {
          request_id: requestId,
          job_id: jobData.google.job.name,
          event_type: "APPLICATION_FINISH",
        });
      }
    }
  }, [isSubmitted]);

  const _submitForm = () => {
    //https://developers.greenhouse.io/job-board.html#submit-an-application
    const errors: Partial<
      FormikProps<Components.Schemas.JobApplication>["errors"] &
        Record<string, string>
    > = {};

    if (!formik.values.first_name.trim().length) {
      errors.first_name = "Required";
    }
    if (!formik.values.last_name.trim().length) {
      errors.last_name = "Required";
    }
    if (!formik.values.email.trim().length) {
      errors.email = "Required";
    } else if (!EMAIL_REGEX.test(formik.values.email.trim())) {
      errors.email = "Valid email address required";
    }

    if (
      jobData?.greenhouse?.questions?.some(
        (question) =>
          question.required &&
          question.fields.some((field) => field.name === "phone")
      ) &&
      (formik.values as unknown as Record<string, string>).phone?.replace(
        /\D/g,
        ""
      ).length !== 10
    ) {
      errors.phone = "Valid 10 digit phone number is required";
    }

    if (jobData?.greenhouse?.questions) {
      jobData.greenhouse.questions.forEach((question) => {
        const key = question.fields
          .at(0)
          ?.name?.replace(
            /\[\]$/i,
            ""
          ) as keyof FormikProps<Components.Schemas.JobApplication>["values"];

        if (
          key &&
          question.required &&
          !errors[key] &&
          typeof formik.values[key] !== "number" &&
          !formik.values[key]?.length &&
          !formik.values[`${key}_content` as typeof key]?.length
        ) {
          errors[key] = "Required";
        }
      });
    }

    formik.setErrors(errors);

    const errorLength = Object.keys(errors).length;

    if (!errorLength) {
      formik.handleSubmit();
    } else {
      analyticsEvent(
        "position_apply_validation_error",
        createJobPostingEventMetaData(jobData!)
      );

      window.notifyUser("error", {
        message: "Required Fields Invalid",
        description: `${errorLength} required ${
          errorLength === 1 ? "field" : "fields"
        } are invalid. Please review the application and resubmit.`,
      });
    }
  };

  useEffect(() => {
    setIsLoading(true);

    api["jobs.get"](jobId)
      .then(({ data }) => {
        setJobData(data);
        setIsLoading(false);

        const locations: string[] =
          data.google.job.derivedInfo.locations.reduce((acc, item) => {
            const str = [
              item?.postalAddress?.locality,
              item?.postalAddress?.administrativeArea,
            ]
              .filter(Boolean)
              .join(", ");
            if (str && !acc.includes(str)) {
              acc.push(str);
            }

            return acc;
          }, []);
        setJobLocations(locations);

        if (requestId) {
          api["jobs.event.create"](null, {
            request_id: requestId,
            job_id: data.google.job.name,
            event_type: "VIEW",
          });
        }
      })
      .catch((err) => {
        setIsLoading(false);
      });
  }, [jobId]);

  const handleClick = () => {
    if (targetRef.current) {
      // @ts-expect-error doesn't like scrollIntoView
      targetRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  if (hasAuthorization !== true) {
    if (isDevelopment) {
      console.error("Unauthorized to access applet.");
    }

    return null;
  }

  return (
    <AppletWrapper appletName="job-posting">
      <div
        style={{
          margin: "auto",
          padding: "0.5rem",
          maxWidth: "80rem",
          minHeight: "20rem",
          minWidth: "20rem",
        }}
      >
        {jobData?.greenhouse?.internal_job_id && (
          <JobPostingSchema jobPostingJson={jobData} />
        )}
        <Header
          jobData={jobData}
          handleClick={handleClick}
          isLoading={isLoading}
        />

        <Row justify="center" gutter={32} style={{ marginTop: "1.5rem" }}>
          <Col xs={24} lg={16}>
            {/* Set's Applicant Description to Rich Text from Greenhouse */}
            {!jobData?.greenhouse ? (
              <Skeleton active={true} />
            ) : (
              <Form id="apply-form" layout="vertical">
                <Row>
                  <Col span={24}>
                    <RichTextContainer>
                      {jobLocations.length > 0 && (
                        <>
                          <h2>{"Location"}</h2>
                          <p>{jobLocations.join("; ")}</p>
                        </>
                      )}
                      {(jobData.google.job.customAttributes.work_environment
                        ?.stringValues?.length || 0) > 0 && (
                        <>
                          <h2>{"Job Type"}</h2>
                          <p>
                            {jobData.google.job.customAttributes.work_environment?.stringValues
                              ?.map(
                                (value) => JOB_TYPE_BE_TO_FE_MAP[value] || value
                              )
                              .join("; ")}
                          </p>
                        </>
                      )}
                      <SanitizedHtml
                        dirtyHtml={htmlDecode(jobData.greenhouse.content)}
                        sanitizeHtmlOptions={{
                          transformTags: {
                            h1: "h2",
                          },
                        }}
                      />
                    </RichTextContainer>
                  </Col>
                  <Col span={24} ref={targetRef}>
                    <H2ApplyTitle id="apply" $breakpoints={breakpoints}>
                      {"Apply for Position"}
                    </H2ApplyTitle>

                    {!isSubmitted &&
                      jobData.greenhouse.questions?.map((question) => {
                        const name = (question.fields
                          .at(0)!
                          .name?.replace(/\[\]$/, "") ||
                          "") as keyof FormikProps<Components.Schemas.JobApplication>["errors"];

                        if (name) {
                          return (
                            <Form.Item
                              key={question.label}
                              label={question.label}
                              required={question.required}
                              validateStatus={
                                formik.errors[name] ? "error" : "success"
                              }
                              help={formik.errors[name]}
                            >
                              <AnswerInput
                                question={question}
                                formik={formik}
                              />
                            </Form.Item>
                          );
                        }
                      })}
                  </Col>

                  <Col span={24}>
                    {!isSubmitted &&
                      Array.isArray(jobData.greenhouse.compliance) &&
                      jobData.greenhouse.compliance.length && (
                        <Modal
                          className="open-position_rich-text"
                          open={showQuestions}
                          centered={true}
                          onCancel={() => setShowQuestions(false)}
                          onOk={() => setShowQuestions(false)}
                          destroyOnClose={true}
                          maskClosable={false}
                          cancelButtonProps={{ style: { display: "none" } }}
                          closeIcon={false}
                          styles={{
                            body: {
                              overflowY: "scroll",
                            },
                          }}
                        >
                          <ConfigProvider
                            theme={
                              APPLET_MANIFEST["job-posting"].themeName
                                ? themeConfig[
                                    APPLET_MANIFEST["job-posting"].themeName
                                  ]
                                : undefined
                            }
                            locale={enUS}
                          >
                            {/* since modal is a react portal, external styles will cascade in--wrap with GlobalStyles to reset */}
                            <GlobalStyles>
                              <RichTextContainer>
                                <ComplianceQuestions
                                  complianceQuestions={
                                    jobData.greenhouse.compliance
                                  }
                                  formik={formik}
                                />
                              </RichTextContainer>
                            </GlobalStyles>
                          </ConfigProvider>
                        </Modal>
                      )}

                    {isSubmitted ? (
                      <Result
                        status="success"
                        title="Application Submitted"
                        subTitle="Thank you for applying! We have received your application and will be reviewing it shortly."
                        className="fadeInFast"
                      />
                    ) : (
                      <Flex
                        wrap="wrap"
                        gap="middle"
                        style={{ marginTop: "1rem" }}
                      >
                        {Array.isArray(jobData.greenhouse.compliance) &&
                          jobData.greenhouse.compliance.length && (
                            <Button
                              type="primary"
                              size="large"
                              ghost={true}
                              onClick={() => {
                                setShowQuestions(!showQuestions);
                                analyticsEvent(
                                  "position_apply_popup_voluntary",
                                  createJobPostingEventMetaData(jobData)
                                );
                              }}
                            >
                              {"Answer Voluntary Questions"}
                            </Button>
                          )}

                        <Button
                          size="large"
                          type="primary"
                          onClick={_submitForm}
                          loading={formik.isSubmitting}
                        >
                          {"Submit Application"}
                        </Button>
                      </Flex>
                    )}
                  </Col>
                </Row>
              </Form>
            )}
          </Col>
        </Row>
      </div>
    </AppletWrapper>
  );
};

export const mount = async (el: HTMLDivElement) => {
  const jobId = getUrlJobPostingId(el);

  if (jobId) {
    const root = createRoot(el);

    root.render(
      <Applet
        jobId={jobId}
        requestId={
          new URLSearchParams(window.location.search).get("requestId") ||
          undefined
        }
      />
    );
  } else {
    console.error("jobId was not defined for JobPosting.mount()");
  }
};
